import DOMPurify from "dompurify";
import { marked, Renderer, Tokens } from "marked";
import { emojify } from "PFCore/helpers/emojify";
import { gidToPath } from "PFCore/helpers/gid";
import { startsWith } from "underscore.string";

const LINK_MENTION_RE =
  /^[!@]{0,1}?\[((?:\[[^\]]*\]|[^[\]]|\](?=[^[]*\]))*)\]\(\s*<?([\s\S]*?)>?(?:\s+['"]([\s\S]*?)['"])?\s*\)/;
const LINK_TEXT_RE = /^[\s\S]+?(?=[\\<![_*`~@$]|https?:\/\/| *\n|$)/;

marked.Lexer.rules.inline.breaks.link = LINK_MENTION_RE;
marked.Lexer.rules.inline.breaks.text = LINK_TEXT_RE;

// Mentions renderer gid profile links
// $[<text>](gid://profinda-api/Profile/<Id>)
//
const mentions = (renderer: Renderer, args: Tokens.Link) => {
  const data = args.href.replace(/^gid:\/\/profinda-api\//, "").split("/");
  const path = gidToPath(args.href as any);
  const tokens = [...args.tokens] as unknown as Tokens.Link;
  tokens[0].text = `@${args.text}`;
  const tag = marked.Renderer.prototype.link.call(renderer, {
    ...args,
    href: path,
    tokens
  });
  return tag.replace(
    /^<a/,
    `<a data-js-mention='true'
      data-js-mention-type='${data[0]}'
      data-js-mention-id='${data[1]}'`
  );
};

const renderer = {
  link(args: Tokens.Link) {
    if (args.href.match(/^gid:\/\/profinda-api\/Profile\/[0-9]+/)) {
      return mentions(this, args);
    }
    const link = marked.Renderer.prototype.link.call(this, args).replace(/href="([^"]+)"/, (match, url) => {
      if (
        !(
          startsWith(url, "http://") ||
          startsWith(url, "https://") ||
          startsWith(url, "/") ||
          startsWith(url, "mailto:")
        )
      ) {
        url = `http://${url}`;
      }

      // adds target=_blank to 3rd party links
      if (!startsWith(url, "#/")) {
        return `href='${url}' target='_blank'`;
      } else {
        return `href='${url}'`;
      }
    });
    return link;
  }
};

const defaultOptions = {
  renderer,
  gfm: true,
  tables: true,
  breaks: true,
  pedantic: false
};

marked.use(defaultOptions);

const SANITIZE_CONFIG = {
  ADD_ATTR: ["data-js-mention", "data-js-mention-type", "data-js-mention-id"]
};

export const preprocessMarkdown = (text: string, shouldEmojify: boolean): string => {
  const out = shouldEmojify ? emojify(text, { careful: true }) : text;

  return out.replace(/([^ `\n])`([^ `\n])/g, "$1&#96;$2");
};

export const postprocessHtml = (html: string, shouldEmojify: boolean): string => {
  const out = shouldEmojify ? emojify(html) : html;

  return DOMPurify.sanitize(out, SANITIZE_CONFIG)
    .replace(/<a/g, " <a rel='noopener noreferrer'")
    .replace(/&amp;/g, "&");
};

export const fullMarkdown = (text?: string, options = { emojify: true }) => {
  if (!text) {
    return "";
  }

  const processed = preprocessMarkdown(text, options.emojify);
  const html = marked.parse(processed) as string;

  return postprocessHtml(html, options.emojify);
};
